import React, { createContext, useContext, useState, useEffect } from "react";
import { portfolioData } from "../data/portfolioData";
import { 
  loadPortfolioFromFirestore, 
  savePortfolioToFirestore, 
  saveSectionToFirestore
} from "../utils/portfolioFirestore";
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from "firebase/auth";
import { auth } from "../config/firebase";

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [portfolioDataState, setPortfolioDataState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    // Listen to Firebase Auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuthenticated(true);
        setCurrentUser(user);
      } else {
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    });

    // Load portfolio data from Firestore only
    const loadPortfolioData = async () => {
      setIsLoading(true);
      try {
        // Load data from Firestore
        const data = await loadPortfolioFromFirestore();
        
        if (data && Object.keys(data).length > 0) {
          setPortfolioDataState(data);
        } else {
          // No data found, use default from local file
          console.log('No data in Firestore, using default data');
          setPortfolioDataState(portfolioData);
        }
      } catch (error) {
        console.error("Error loading portfolio data from Firestore:", error);
        // Use default data as fallback
        setPortfolioDataState(portfolioData);
      } finally {
        setIsLoading(false);
      }
    };

    loadPortfolioData();

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      let errorMessage = "Invalid credentials";
      
      if (error.code === 'auth/invalid-credential') {
        errorMessage = "Invalid email or password";
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = "User not found";
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = "Incorrect password";
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = "Too many failed attempts. Please try again later.";
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = "Network error. Please check your connection.";
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setIsAdminMode(false);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const toggleAdminMode = () => {
    setIsAdminMode(!isAdminMode);
  };

  const updatePortfolioData = async (newData) => {
    setPortfolioDataState(newData);
    // Save to Firestore only
    try {
      await savePortfolioToFirestore(newData);
    } catch (error) {
      console.error('Error saving portfolio data:', error);
      throw error;
    }
  };

  const updateSection = async (section, data) => {
    const updatedData = {
      ...portfolioDataState,
      [section]: data,
    };
    setPortfolioDataState(updatedData);
    // Save specific section to Firestore only
    try {
      await saveSectionToFirestore(section, data);
    } catch (error) {
      console.error(`Error saving ${section} section:`, error);
      throw error;
    }
  };

  const updateProject = (projectId, updatedProject) => {
    const updatedProjects = portfolioDataState.projects.items.map(
      (project, index) => (index === projectId ? updatedProject : project)
    );
    updateSection("projects", {
      ...portfolioDataState.projects,
      items: updatedProjects,
    });
  };

  const addProject = (newProject) => {
    const updatedProjects = [...portfolioDataState.projects.items, newProject];
    updateSection("projects", {
      ...portfolioDataState.projects,
      items: updatedProjects,
    });
  };

  const deleteProject = (projectId) => {
    const updatedProjects = portfolioDataState.projects.items.filter(
      (_, index) => index !== projectId
    );
    updateSection("projects", {
      ...portfolioDataState.projects,
      items: updatedProjects,
    });
  };

  const updateExperience = (experienceId, updatedExperience) => {
    const updatedExperiences = portfolioDataState.experience.items.map(
      (exp, index) => (index === experienceId ? updatedExperience : exp)
    );
    updateSection("experience", {
      ...portfolioDataState.experience,
      items: updatedExperiences,
    });
  };

  const addExperience = (newExperience) => {
    const updatedExperiences = [
      ...portfolioDataState.experience.items,
      newExperience,
    ];
    updateSection("experience", {
      ...portfolioDataState.experience,
      items: updatedExperiences,
    });
  };

  const deleteExperience = (experienceId) => {
    const updatedExperiences = portfolioDataState.experience.items.filter(
      (_, index) => index !== experienceId
    );
    updateSection("experience", {
      ...portfolioDataState.experience,
      items: updatedExperiences,
    });
  };

  const updatePersonalInfo = (updatedPersonal) => {
    updateSection("personal", updatedPersonal);
  };

  const resetToDefault = async () => {
    setPortfolioDataState(portfolioData);
    // Save default to Firestore only
    try {
      await savePortfolioToFirestore(portfolioData);
    } catch (error) {
      console.error('Error resetting to default:', error);
      throw error;
    }
  };

  const value = {
    isAuthenticated,
    isAdminMode,
    portfolioData: portfolioDataState,
    isLoading,
    currentUser,
    login,
    logout,
    toggleAdminMode,
    updatePortfolioData,
    updateSection,
    updateProject,
    addProject,
    deleteProject,
    updateExperience,
    addExperience,
    deleteExperience,
    updatePersonalInfo,
    resetToDefault,
  };

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};
